import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TextField from '../TextField';

import { EligibilityFormCustomization } from '@/pages/Onboarding/OnboardingFlowContext';
import { formatDateInput } from '@/utils/inputHelpers';

type Props = {
  formCustomization: EligibilityFormCustomization;
  /** Renders the email field, third in order (after last name, before zip code). */
  showEmail?: boolean;
};

const CoverageFields = ({ formCustomization, showEmail = false }: Props) => {
  const { t } = useTranslation();
  const {
    showZipCode, showEmployeeId, employeeIdFieldRename, employeeIdExample, employeeIdValidationEnabled,
  } = formCustomization;
  const employeeIdField = employeeIdFieldRename
    || t('coverage.sleepio.patient.employee_id_label');

  const generatePlaceHolder = useCallback((label: string) => (
    `${t('coverage.enter')} ${label.toLocaleLowerCase()}...`
  ), [t]);

  const { formState: { errors }, clearErrors } = useFormContext();

  // No t() key below is org-controlled, so i18next's ':' namespace-split can't truncate one.
  return (
    <>
      <Controller
        name="firstName"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="first-name-input"
            required
            label={t('coverage.sleepio.patient.f_name_label')}
            placeholder={generatePlaceHolder(t('coverage.sleepio.patient.f_name_label'))}
            helperText={errors?.firstName?.message && t(errors.firstName.message as string)}
            error={!!errors?.firstName}
            onFocus={() => clearErrors('firstName')}
          />
        )}
      />
      <Controller
        name="lastName"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="last-name-input"
            required
            label={t('coverage.sleepio.patient.l_name_label')}
            placeholder={generatePlaceHolder(t('coverage.sleepio.patient.l_name_label'))}
            helperText={errors?.lastName?.message && t(errors.lastName.message as string)}
            error={!!errors?.lastName}
            onFocus={() => clearErrors('lastName')}
          />
        )}
      />
      {showEmail && (
        <Controller
          name="email"
          render={({ field }) => (
            <TextField
              {...field}
              required
              data-testid="email-input"
              label={t('coverage.sleepio.patient.email_label')}
              placeholder={generatePlaceHolder(t('coverage.sleepio.patient.email_label'))}
              helperText={errors?.email?.message && t(errors.email.message as string)}
              error={!!errors?.email}
              onFocus={() => clearErrors('email')}
            />
          )}
        />
      )}
      {showZipCode && (
        <Controller
          name="zipCode"
          render={({ field }) => (
            <TextField
              {...field}
              required
              data-testid="zip-code-input"
              id="confimDetailsFormZipCode"
              label={t('coverage.sleepio.patient.zipcode_label')}
              placeholder={generatePlaceHolder(t('coverage.sleepio.patient.zipcode_label'))}
              helperText={errors?.zipCode?.message && t(errors.zipCode.message as string)}
              error={!!errors?.zipCode}
              onFocus={() => clearErrors('zipCode')}
              inputProps={{
                maxLength: 5,
                inputMode: 'numeric',
              }}
            />
          )}
        />
      )}
      {showEmployeeId && (
        <Controller
          name="employeeId"
          render={({ field }) => (
            <TextField
              {...field}
              required
              data-testid="employee-id-input"
              label={employeeIdField}
              placeholder={
                (employeeIdValidationEnabled && employeeIdExample)
                  ? employeeIdExample
                  : generatePlaceHolder(employeeIdField)
              }
              helperText={errors?.employeeId?.message && t(errors.employeeId.message as string)}
              error={!!errors?.employeeId}
              onFocus={() => clearErrors('employeeId')}
            />
          )}
        />
      )}
      <Controller
        name="dateOfBirth"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="dob-input"
            required
            label={t('coverage.sleepio.patient.dob_label')}
            placeholder="MM / DD / YYYY"
            helperText={errors?.dateOfBirth?.message && t(errors.dateOfBirth.message as string)}
            error={!!errors?.dateOfBirth}
            onFocus={() => clearErrors('dateOfBirth')}
            inputProps={{
              maxLength: 14,
              inputMode: 'numeric',
            }}
            onChange={(e) => {
              const value = formatDateInput(e.target.value);
              field.onChange(value);
            }}
          />
        )}
      />
    </>
  );
};

export default CoverageFields;
